import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { UserAvatar } from './UserAvatar';
import { Camera, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export const AvatarUploadDialog = ({ open, onOpenChange, user, onAvatarChanged }) => {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
      toast.error('Solo se permiten imágenes JPG o PNG');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('La imagen no debe exceder 2MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/users/${user.id}/avatar`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Foto actualizada');
      onAvatarChanged?.(res.data.avatar_url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir la foto');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    setUploading(true);
    try {
      await api.delete(`/users/${user.id}/avatar`);
      toast.success('Foto eliminada');
      onAvatarChanged?.(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar la foto');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="avatar-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Manrope']">Foto de perfil</DialogTitle>
          <p className="text-sm text-slate-500">Sube o cambia tu foto de perfil</p>
        </DialogHeader>
        <div className="flex flex-col items-center gap-6 py-4">
          <div className="relative group">
            <UserAvatar name={user?.name} avatarUrl={user?.avatar_url} size="xl" />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="absolute inset-0 bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
              data-testid="avatar-camera-overlay"
            >
              <Camera className="w-6 h-6 text-white" />
            </button>
          </div>
          <div className="text-center">
            <p className="font-medium text-slate-900">{user?.name}</p>
            <p className="text-sm text-slate-500">{user?.email}</p>
          </div>
          <div className="flex gap-3 w-full">
            <Button
              className="flex-1"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="avatar-upload-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'Subiendo...' : 'Subir foto'}
            </Button>
            {user?.avatar_url && (
              <Button
                variant="outline"
                onClick={handleRemove}
                disabled={uploading}
                data-testid="avatar-remove-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Eliminar
              </Button>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png"
            className="hidden"
            onChange={handleFileSelect}
          />
          <p className="text-xs text-slate-400 text-center">
            JPG, JPEG o PNG, máximo 2MB
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AvatarUploadDialog;
